import React, { ReactElement, useEffect } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  UseFormReturn,
  UseFormStateReturn,
} from 'react-hook-form';
import { ScheduledEventForm } from '../models/ScheduledEventDto';
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { ScheduleEventDeviceConfigType } from '@thingslog/repositories';
import { scheduledEventFactory } from '../utils';
import { useTranslation } from 'react-i18next';
import ScheduledEventFormFieldsMapper from './ScheduledEventFormFieldsMapper';
import { useViewport } from '../../../hooks';

export const ScheduledEventFormUI: React.FC<ScheduledEventFormProps> = ({
  form,
  onSubmit,
  mode,
}: ScheduledEventFormProps) => {
  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isValid },
    watch,
    setValue,
  } = form;

  const { t } = useTranslation();
  const isMobile = useViewport('sm');

  const handleEventTypeChange = (event: SelectChangeEvent<ScheduleEventDeviceConfigType>): void => {
    const currentEvent = watch('event');
    const currentSelectedDevices = currentEvent.deviceNumbers;
    const scheduledEventType = event.target.value as ScheduleEventDeviceConfigType;
    const factory = scheduledEventFactory[scheduledEventType];
    setValue('event', factory.create(currentSelectedDevices), { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-4 gap-x-2 gap-y-4 px-3 py-3">
      <div className="col-span-4 flex items-center justify-center">
        <Typography
          variant={isMobile ? 'body1' : 'subtitle1'}
          className="text-center font-semibold"
        >
          {mode === 'create'
            ? t('scheduled_task_event_form_new_scheduled_event')
            : t('scheduled_task_event_form_edit_scheduled_event')}
        </Typography>
      </div>

      {/* Event Name */}
      <div className="col-span-4 md:col-span-2">
        <TextField
          fullWidth={true}
          label={t('scheduled_tasks_list_event_name')}
          size="small"
          InputLabelProps={{ shrink: true }}
          {...register('eventName')}
          error={!!errors.eventName}
          helperText={errors.eventName?.message ?? ''}
        />
      </div>

      {/* Event Type */}
      <div className="col-span-4 md:col-span-2">
        <FormControl fullWidth size="small">
          <InputLabel id="event-type-label">{t('scheduled_task_event_form_event_type')}</InputLabel>
          <Controller
            name="event.@type"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<ScheduledEventForm, 'event.@type'>;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                onChange={handleEventTypeChange}
                labelId="event-type-label"
                label={t('scheduled_task_event_form_event_type')}
              >
                <MenuItem value={'SINGLE_RELAY_SWITCH'}>
                  {t('command_type_single_relay_switch')}
                </MenuItem>
                <MenuItem value={'RELAY_SWITCH'}>{t('command_type_relay_switch')}</MenuItem>
                <MenuItem value={'AUTO_RELAY_SWITCH'}>
                  {t('command_type_auto_relay_switch')}
                </MenuItem>
                <MenuItem value={'SEND_DATE_OVER_MQTT'}>
                  {t('command_type_send_date_over_mqtt')}
                </MenuItem>
                <MenuItem value={'MODBUS_READ_COIL'}>
                  {t('command_type_modbus')}{' '}
                  {t('scheduled_task_event_form_modbus_command_type_read_coil_1')}
                </MenuItem>
                <MenuItem value={'MODBUS_READ_REGISTER'}>
                  {t('command_type_modbus')}{' '}
                  {t('scheduled_task_event_form_modbus_command_type_read_register_3')}
                </MenuItem>
                <MenuItem value={'MODBUS_WRITE_COIL'}>
                  {t('command_type_modbus')}{' '}
                  {t('scheduled_task_event_form_modbus_command_type_write_coil_5')}
                </MenuItem>
                <MenuItem value={'TASMOTA_POWER'}>{t('command_type_tasmota_power')}</MenuItem>
                <MenuItem value={'SCHEDULED_RELAY_COMMAND'}>
                  {t('command_type_scheduled_relay_command')}
                </MenuItem>
                <MenuItem value={'SCHEDULED_RELAY_COMMAND_4'}>
                  {t('command_type_scheduled_relay_command_4')}
                </MenuItem>
              </Select>
            )}
          />
          {errors.event?.['@type'] && (
            <FormHelperText>{errors.event['@type'].message}</FormHelperText>
          )}
        </FormControl>
      </div>
      {/* FormFields*/}
      <ScheduledEventFormFieldsMapper form={form} />

      {/* Submit Button */}
      <div className="col-span-4">
        {errors.event?.deviceNumbers && (
          <p className="text-red-500 ml-10">{errors.event.deviceNumbers.message}</p>
        )}
        <Button fullWidth variant="contained" disabled={!isValid} type="submit">
          {mode === 'create' ? t('create') : t('update')}
        </Button>
      </div>
    </form>
  );
};

interface ScheduledEventFormProps {
  form: UseFormReturn<ScheduledEventForm, unknown, undefined>;
  mode: 'create' | 'edit';
  onSubmit: (data: ScheduledEventForm) => void;
}
